import { resolvePmTradeProtocol } from '@/domain/market/trade-protocol';
import { resolveMarketVariant, isBinaryOutcomes } from '@/utils/market-variant';
import {
  getBinaryOutcomeValues,
  parseMarketOutcomes,
  parseOutcomePrices,
} from '@/utils/market-outcomes';
import { resolveMarketTradeSymbol } from '@/utils/market-detail-map';
import { resolveOrderSymbol } from '@/utils/trade-order';
import {
  filterListedMarkets,
  getFirstListedMarket,
  hasListedCollectionMarkets,
} from '@/utils/market-list-status.js';
import { resolvePolymarketDisplayLabels } from '@/lib/polymarket/resolvePolymarketDisplayLabels.js';
import {
  complementBinaryYes,
  exclusiveYesProbsForMarkets,
  normalizeMultiOutcomeProbs,
} from '@/domain/market/exclusive-outcome-odds.js';

function parseJsonOrCsv(str) {
  return parseMarketOutcomes(str);
}

/**
 * 卡片子行标签：批量 A(groupItemTitle)+B(兄弟差分)；单条失败再回落集合 title。
 * @param {object[]} markets
 * @param {object} collectionRow
 * @param {string} [locale]
 */
function resolveCardMarketLabels(markets, collectionRow, locale = 'zh') {
  const labels = resolvePolymarketDisplayLabels(markets, locale);
  const fallback = String(collectionRow?.title || '').trim() || '—';
  return labels.map((label) => (label && label !== '—' ? label : fallback));
}

function parseBinaryPercents(market, yesOverride) {
  // null = not in exclusive family; Number(null)===0 would paint Yes 0%.
  if (yesOverride != null && Number.isFinite(Number(yesOverride))) {
    const { yes, no } = complementBinaryYes(Number(yesOverride));
    return { yes: Math.round(yes * 100), no: Math.round(no * 100) };
  }
  const prices = parseOutcomePrices(market?.outcomePrices);
  if (prices.length >= 2) {
    const { yes, no } = complementBinaryYes(prices[0] > 1 ? prices[0] / 100 : prices[0]);
    return { yes: Math.round(yes * 100), no: Math.round(no * 100) };
  }
  const p = Number(market?.lastTradePrice);
  if (Number.isFinite(p) && p > 0 && p <= 1) {
    const { yes, no } = complementBinaryYes(p);
    return { yes: Math.round(yes * 100), no: Math.round(no * 100) };
  }
  return { yes: 50, no: 50 };
}

function outcomePriceToPercent(price, fallbackCount) {
  const num = Number(price);
  if (!Number.isFinite(num)) return Math.max(1, Math.round(100 / fallbackCount));
  const pct = num > 0 && num <= 1 ? num * 100 : num;
  let rounded = Math.round(pct);
  if (rounded <= 0) rounded = 1;
  if (rounded >= 100) rounded = 99;
  return rounded;
}

function parseMultiOptions(market, serverProjected = false) {
  const labels = parseJsonOrCsv(market?.outcomes);
  const prices = parseOutcomePrices(market?.outcomePrices);
  if (!labels.length) {
    return [{ label: '—', percent: 50, outcome: 'Yes' }];
  }
  const rawPrices = labels.map((_, i) => (Number.isFinite(prices[i]) ? prices[i] : 0.5));
  const exclusive = serverProjected ? rawPrices : normalizeMultiOutcomeProbs(rawPrices);
  return labels.map((label, i) => {
    const raw = exclusive[i] ?? prices[i];
    const outcome = String(label).trim();
    const outcomePrice = Number.isFinite(raw) ? raw : undefined;
    return {
      label: outcome,
      percent: outcomePriceToPercent(raw, labels.length),
      outcome,
      outcomePrice,
    };
  });
}

function resolveMarketVolume24hr(market) {
  const n = Number(market?.volume24hr ?? market?.volume ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** 多二元子 market 行（labels 为 API 原始 outcome 文案，i18n 在 presentation） */
export function mapCollectionCardMarkets(row, locale = 'zh') {
  const markets = filterListedMarkets(row?.markets).filter((m) => isBinaryOutcomes(m?.outcomes));
  const labels = resolveCardMarketLabels(markets, row, locale);
  const exclusiveYes = exclusiveYesProbsForMarkets(markets, row);
  return markets
    .map((m, idx) => {
      const { yes, no } = parseBinaryPercents(m, exclusiveYes[idx]);
      const outcomeValues = parseMarketOutcomes(m?.outcomes);
      const raw = getBinaryOutcomeValues(outcomeValues);
      return {
        marketIndex: idx,
        orderSymbol:
          resolveMarketTradeSymbol(m) || resolveOrderSymbol({ collection: row, market: m }),
        label: labels[idx],
        yesPercent: yes,
        noPercent: no,
        yesLabel: raw.yes,
        noLabel: raw.no,
        outcomeValues,
        outcomesRaw: m?.outcomes ?? (outcomeValues.length >= 2 ? outcomeValues : undefined),
        volume24hr: resolveMarketVolume24hr(m),
        pmProtocolVersion: resolvePmTradeProtocol(m),
        tradingCollateral: String(m?.tradingCollateral ?? m?.trading_collateral ?? '')
          .trim()
          .toUpperCase(),
        exchangeAddress: String(
          m?.exchangeAddress ?? m?.exchange_address ?? m?.exchange ?? ''
        ).trim(),
      };
    })
    .sort((a, b) => b.volume24hr - a.volume24hr);
}

/** 多多元子 market 行（outcomes > 2，i18n 在 presentation） */
export function mapCollectionCardMultiMarkets(row, locale = 'zh') {
  const markets = filterListedMarkets(row?.markets).filter((m) => {
    const count = parseMarketOutcomes(m?.outcomes).length;
    return !isBinaryOutcomes(m?.outcomes) && count > 2;
  });
  const labels = resolveCardMarketLabels(markets, row, locale);
  return markets
    .map((m, idx) => {
      return {
        marketIndex: idx,
        orderSymbol:
          resolveMarketTradeSymbol(m) || resolveOrderSymbol({ collection: row, market: m }),
        label: labels[idx],
        multiOptions: parseMultiOptions(m, row?.marketPreview === true || row?.sportsFeed === true),
        outcomeValues: parseMarketOutcomes(m?.outcomes),
        volume24hr: resolveMarketVolume24hr(m),
        pmProtocolVersion: resolvePmTradeProtocol(m),
        tradingCollateral: String(m?.tradingCollateral ?? m?.trading_collateral ?? '')
          .trim()
          .toUpperCase(),
        exchangeAddress: String(
          m?.exchangeAddress ?? m?.exchange_address ?? m?.exchange ?? ''
        ).trim(),
      };
    })
    .sort((a, b) => b.volume24hr - a.volume24hr);
}

/**
 * 多 markets 集合：若存在 outcomes≥3 的多元子 market，列表卡只精选成交量最高的一条。
 * @returns {{ orderSymbol: string, multiOptions: object[], outcomeValues: string[] } | null}
 */
export function resolveFeedFeaturedMultiMarket(row, { maxOutcomes = 3 } = {}) {
  const markets = filterListedMarkets(row?.markets);
  if ((row?.marketPreview === true ? Number(row.marketCount) : markets.length) <= 1) return null;

  const multiRows = mapCollectionCardMultiMarkets(row);
  if (!multiRows.length) return null;

  const featured = multiRows[0];
  const cap = Math.max(1, Number(maxOutcomes) || 3);
  return {
    orderSymbol: featured.orderSymbol,
    multiOptions: (featured.multiOptions || []).slice(0, cap),
    outcomeValues: featured.outcomeValues || [],
    pmProtocolVersion: featured.pmProtocolVersion || '',
    tradingCollateral: featured.tradingCollateral || '',
    exchangeAddress: featured.exchangeAddress || '',
  };
}

export function resolveCollectionVolume(row) {
  const market = getFirstListedMarket(row?.markets);
  const raw = row?.volume ?? market?.volume ?? market?.volume24hr ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** collection 是否带有可展示的上架 markets 数据 */
export function hasCollectionMarkets(row) {
  return hasListedCollectionMarkets(row);
}

export function sortCollectionRowsByVolume(rows) {
  return [...rows].sort((a, b) => resolveCollectionVolume(b) - resolveCollectionVolume(a));
}

export function mapExploreCollectionRow(row, { formatVolumeValue, categoryLabel }) {
  const market = getFirstListedMarket(row?.markets);
  const variant = resolveMarketVariant({
    questionType: row.questionType || market?.questionType,
    closed: row.closed ?? market?.closed,
    active: row.active ?? market?.active,
    endDate: row.endDate ?? market?.endDate,
    outcomes: market?.outcomes,
    markets: row?.markets,
    marketStatus: market?.marketStatus ?? row.marketStatus,
  });
  const base = {
    id: String(row.id ?? '').trim(),
    orderSymbol: resolveOrderSymbol({ collection: row, market }),
    imageUrl: String(row.image || row.icon || market?.image || market?.icon || '').trim(),
    title: String(row.title || market?.question || '').trim() || '—',
    volume: formatVolumeValue(resolveCollectionVolume(row)),
    category: categoryLabel(String(row.category || '').trim()),
    variant,
    multiOptions: [],
    yesPercent: 0,
    noPercent: 0,
    yesLabel: '',
    noLabel: '',
    outcomeValues: [],
    marketRows: [],
    pmProtocolVersion: resolvePmTradeProtocol(market),
    tradingCollateral: String(market?.tradingCollateral ?? market?.trading_collateral ?? '')
      .trim()
      .toUpperCase(),
    exchangeAddress: String(
      market?.exchangeAddress ?? market?.exchange_address ?? market?.exchange ?? ''
    ).trim(),
  };

  if (variant === 'multi') {
    base.multiOptions = parseMultiOptions(
      market,
      row?.marketPreview === true || row?.sportsFeed === true
    );
    base.outcomeValues = parseMarketOutcomes(market?.outcomes);
  } else if (variant === 'binary') {
    const binaries = filterListedMarkets(row?.markets).filter((m) => isBinaryOutcomes(m?.outcomes));
    const exclusiveYes = exclusiveYesProbsForMarkets(binaries, row);
    const idx = binaries.findIndex((m) => String(m?.id ?? '') === String(market?.id ?? ''));
    const { yes, no } = parseBinaryPercents(market, idx >= 0 ? exclusiveYes[idx] : null);
    base.yesPercent = yes;
    base.noPercent = no;
    const outcomeValues = parseMarketOutcomes(market?.outcomes);
    base.outcomeValues = outcomeValues;
    const labels = getBinaryOutcomeValues(outcomeValues);
    base.yesLabel = labels.yes;
    base.noLabel = labels.no;
  }

  return base;
}
